import { useEffect, useState, useMemo } from "react";
import { api, euro, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle, MessageCircle, Mail, X, Paperclip, Upload, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyLine = { description: "", brand: "", model: "", quantity: 1, unit_price: 0, serials: "" };
const emptyForm = { supplier_id: "", center_id: "", note_number: "", date: "", notes: "", lines: [{ ...emptyLine }] };

const STATUS_LABEL = {
  received: { label: "Recibido", className: "bg-[#D1E0D7] text-[#2A2F2C]" },
  invoiced: { label: "Facturado", className: "bg-[#EAE6DF] text-[#2A2F2C]" },
  discrepancy: { label: "Discrepancia", className: "bg-[#F3D5C8] text-[#7C3A1C]" },
  resolved: { label: "Resuelto", className: "bg-white text-[#6E7772] border" },
};

export default function DeliveryNotes() {
  const { user, centers } = useAuth();
  const [notes, setNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [discrepancies, setDiscrepancies] = useState([]);
  // Adjunto opcional para el formulario de creación
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [parsing, setParsing] = useState(false);

  const onAttachmentSelected = async (f) => {
    setPendingAttachment(f);
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", "delivery_note");
      const { data } = await api.post("/supplier-docs/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({
        ...prev,
        note_number: data.note_number || prev.note_number,
        date: /^\d{4}-\d{2}-\d{2}/.test(data.date || "") ? data.date.slice(0, 10) : prev.date,
        supplier_id: data.supplier_id || prev.supplier_id,
        notes: data.notes || prev.notes,
        lines:
          Array.isArray(data.lines) && data.lines.length
            ? data.lines.map((l) => ({
                description: l.description || "",
                brand: l.brand || "",
                model: l.model || "",
                quantity: parseInt(l.quantity) || 1,
                unit_price: parseFloat(l.unit_price) || 0,
                serials: Array.isArray(l.serials) ? l.serials.join(", ") : "",
              }))
            : prev.lines,
      }));
      if (!data.supplier_id && data.supplier_name) {
        toast.info(`Proveedor detectado: "${data.supplier_name}". No está en tu lista, selecciónalo manualmente.`);
      }
      toast.success("Documento analizado: revisa las líneas rellenadas");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setParsing(false);
    }
  };

  const load = async () => {
    const isMgr = ["admin", "center_admin"].includes(user?.role) || user?.role === "gerente";
    const [ns, ss, ds] = await Promise.all([
      api.get("/delivery-notes").then((r) => r.data).catch(() => []),
      api.get("/suppliers").then((r) => r.data).catch(() => []),
      isMgr
        ? api.get("/delivery-notes/discrepancies/open").then((r) => r.data).catch(() => [])
        : Promise.resolve([]),
    ]);
    setNotes(ns);
    setSuppliers(ss);
    setDiscrepancies(ds);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [user?.role]);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  const openNew = () => {
    setForm({
      ...emptyForm,
      center_id: user?.center_id || centers[0]?.id || "",
      date: new Date().toISOString().slice(0, 10),
      lines: [{ ...emptyLine }],
    });
    setPendingAttachment(null);
    setOpen(true);
  };

  const uploadAttachmentTo = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/delivery-notes/${id}/attachment`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id || !form.center_id) {
      toast.error("Selecciona proveedor y centro");
      return;
    }
    const lines = form.lines
      .filter((l) => (l.description || "").trim() || l.brand || l.model)
      .map((l) => ({
        description: l.description || `${l.brand} ${l.model}`.trim(),
        brand: l.brand || "",
        model: l.model || "",
        quantity: parseInt(l.quantity) || 1,
        unit_price: parseFloat(l.unit_price) || 0,
        serials: (l.serials || "")
          .split(/[\n,;]/)
          .map((s) => s.trim())
          .filter(Boolean),
      }));
    if (lines.length === 0) {
      toast.error("Añade al menos una línea");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/delivery-notes", {
        supplier_id: form.supplier_id,
        center_id: form.center_id,
        note_number: form.note_number,
        date: form.date,
        lines,
        notes: form.notes,
      });
      if (pendingAttachment && data?.id) {
        try {
          await uploadAttachmentTo(data.id, pendingAttachment);
        } catch (upErr) {
          toast.error("Albarán creado, pero no se pudo adjuntar el archivo");
        }
      }
      toast.success("Albarán creado · stock actualizado");
      setOpen(false);
      setPendingAttachment(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar albarán? Se revertirán las unidades sumadas al stock.")) return;
    try {
      await api.delete(`/delivery-notes/${id}`);
      toast.success("Albarán eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markResolved = async (id) => {
    try {
      await api.patch(`/delivery-notes/${id}`, { status: "resolved" });
      toast.success("Marcado como resuelto");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const contactSupplierWA = (dn) => {
    const txt = encodeURIComponent(
      `Hola,\n\nEn la factura ${dn.supplier_invoice_id ? "#" + dn.supplier_invoice_id.slice(0, 8) : ""} hemos detectado diferencias con el albarán ${dn.note_number || dn.id.slice(0, 8)}:\n\n` +
        (dn.discrepancies || []).map((d) => `• ${d.message}`).join("\n") +
        `\n\n¿Podemos revisarlo?\n\nGracias.`,
    );
    const phone = (dn.supplier_phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("Este proveedor no tiene teléfono guardado");
      return;
    }
    window.open(`https://wa.me/${phone}?text=${txt}`, "_blank");
  };

  const contactSupplierEmail = (dn) => {
    const subject = encodeURIComponent(`Discrepancia en albarán ${dn.note_number || dn.id.slice(0, 8)}`);
    const body = encodeURIComponent(
      `Hola,\n\nHemos detectado diferencias entre la factura y el albarán recibido:\n\n` +
        (dn.discrepancies || []).map((d) => `• ${d.message}`).join("\n") +
        `\n\n¿Podemos revisarlo?\n\nGracias.`,
    );
    const email = dn.supplier_email;
    if (!email) {
      toast.error("Este proveedor no tiene email guardado");
      return;
    }
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const [uploadingForId, setUploadingForId] = useState(null);

  const handleRowAttachmentUpload = async (id, file) => {
    if (!file) return;
    setUploadingForId(id);
    try {
      await uploadAttachmentTo(id, file);
      toast.success("Adjunto guardado");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingForId(null);
    }
  };

  const openAttachment = async (id) => {
    try {
      const resp = await api.get(`/delivery-notes/${id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setLine = (idx, k, v) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, [k]: v } : l)),
    }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const totalAmount = useMemo(
    () => form.lines.reduce((acc, l) => acc + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0),
    [form.lines],
  );

  return (
    <div className="space-y-6" data-testid="deliverynotes-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">Almacén</div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Albaranes
          </h1>
          <p className="text-sm text-[#6E7772] mt-2 max-w-2xl">
            Registra cada recepción de mercancía del proveedor. Al guardar, el stock se incrementa
            automáticamente con los números de serie de cada unidad. Si la factura del proveedor
            no coincide, recibirás una alerta.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-delivery-note-btn"
              onClick={openNew}
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo albarán
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nuevo albarán</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="border-2 border-dashed border-[#C9D3CB] rounded-lg p-4 bg-[#F6F7F4]">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-[#4A5D4E]" />
                  <Label className="mb-0 font-medium">
                    Adjunta el PDF / foto del albarán y se rellenará automáticamente
                  </Label>
                </div>
                <Input
                  type="file"
                  accept=".pdf,image/*"
                  data-testid="dn-attachment-input"
                  disabled={parsing}
                  onChange={(e) => onAttachmentSelected(e.target.files?.[0] || null)}
                />
                {parsing && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-[#4A5D4E]" data-testid="dn-parse-spinner">
                    <Loader2 className="h-4 w-4 animate-spin" /> Analizando documento con IA…
                  </div>
                )}
                {pendingAttachment && !parsing && (
                  <div className="text-xs text-[#6E7772] mt-1 flex items-center gap-1">
                    <Paperclip className="h-3 w-3" /> {pendingAttachment.name}
                    <button
                      type="button"
                      className="text-[#C07F67] underline ml-2"
                      onClick={() => setPendingAttachment(null)}
                    >
                      quitar
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Proveedor *</Label>
                  <Select
                    value={form.supplier_id || "none"}
                    onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="dn-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Selecciona —</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Centro *</Label>
                  <Select
                    value={form.center_id || "none"}
                    onValueChange={(v) => setForm({ ...form, center_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {centers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nº albarán (proveedor)</Label>
                  <Input
                    value={form.note_number}
                    onChange={(e) => setForm({ ...form, note_number: e.target.value })}
                    placeholder="Ej. ALB-2026-123"
                  />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
              </div>

              <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAF9F6]">
                      <TableHead>Descripción</TableHead>
                      <TableHead className="w-28">Marca</TableHead>
                      <TableHead className="w-32">Modelo</TableHead>
                      <TableHead className="w-20">Cant.</TableHead>
                      <TableHead className="w-32">P.unit (€)</TableHead>
                      <TableHead>Nº de serie (uno por línea, coma o ;)</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Input
                            value={l.description}
                            onChange={(e) => setLine(i, "description", e.target.value)}
                            placeholder="Ej. Phonak Audeo Lumity P90"
                          />
                        </TableCell>
                        <TableCell>
                          <Input value={l.brand} onChange={(e) => setLine(i, "brand", e.target.value)} placeholder="Phonak" />
                        </TableCell>
                        <TableCell>
                          <Input value={l.model} onChange={(e) => setLine(i, "model", e.target.value)} placeholder="Lumity P90" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" min={1} value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" min={0} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <textarea
                            value={l.serials}
                            onChange={(e) => setLine(i, "serials", e.target.value)}
                            placeholder="PA001, PA002, PA003 — o uno por línea"
                            rows={2}
                            className="w-full px-2 py-1 text-sm border border-[#E5E3DB] rounded bg-white"
                          />
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)}>
                            <X className="h-4 w-4 text-[#C07F67]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-3 flex justify-between items-center bg-[#FAF9F6]">
                  <Button type="button" variant="outline" size="sm" onClick={addLine}>
                    <Plus className="h-3 w-3 mr-1" /> Añadir línea
                  </Button>
                  <div className="text-sm font-medium">Total albarán: {euro(totalAmount)}</div>
                </div>
              </div>

              <div>
                <Label>Notas</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white" data-testid="save-dn-btn">
                  {saving ? "Guardando…" : "Guardar y sumar al stock"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alertas de discrepancia */}
      {discrepancies.length > 0 && (
        <Card className="border-[#C07F67] bg-[#FFF6F0]" data-testid="discrepancies-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-[#C07F67]" />
              <h2 className="font-heading text-lg font-medium text-[#7C3A1C]">
                Discrepancias con facturas — contacta al proveedor
              </h2>
            </div>
            <div className="space-y-3">
              {discrepancies.map((d) => (
                <div key={d.id} className="bg-white border border-[#E5E3DB] rounded-md p-3" data-testid={`discrepancy-${d.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-[#2A2F2C]">
                        {d.supplier_name} · Albarán {d.note_number || d.id.slice(0, 8)} · {formatDate(d.date)}
                      </div>
                      <ul className="mt-1 text-sm text-[#7C3A1C] list-disc list-inside space-y-0.5">
                        {(d.discrepancies || []).map((x, i) => (
                          <li key={i}>{x.message}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {d.supplier_phone && (
                        <Button size="sm" variant="outline" onClick={() => contactSupplierWA(d)} data-testid={`wa-${d.id}`}>
                          <MessageCircle className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                      )}
                      {d.supplier_email && (
                        <Button size="sm" variant="outline" onClick={() => contactSupplierEmail(d)} data-testid={`email-${d.id}`}>
                          <Mail className="h-3 w-3 mr-1" /> Email
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => markResolved(d.id)}
                        className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                        data-testid={`resolve-${d.id}`}
                      >
                        Marcar resuelto
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Listado de albaranes */}
      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E3DB]">
              <TableHead>Fecha</TableHead>
              <TableHead>Nº albarán</TableHead>
              <TableHead>Proveedor</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead>Líneas</TableHead>
              <TableHead className="text-right">Importe</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Adjunto</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notes.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-[#6E7772]">
                  Sin albaranes todavía. Crea el primero al recibir mercancía del proveedor.
                </TableCell>
              </TableRow>
            )}
            {notes.map((n) => {
              const total = (n.lines || []).reduce(
                (a, l) => a + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0),
                0,
              );
              const status = STATUS_LABEL[n.status] || STATUS_LABEL.received;
              const canManage = ["admin", "center_admin"].includes(user?.role) || user?.role === "gerente";
              return (
                <TableRow key={n.id} className="border-[#E5E3DB]" data-testid={`dn-row-${n.id}`}>
                  <TableCell className="text-sm">{formatDate(n.date)}</TableCell>
                  <TableCell className="font-medium">{n.note_number || <span className="text-[#9CA3AF]">—</span>}</TableCell>
                  <TableCell>{supplierName(n.supplier_id)}</TableCell>
                  <TableCell className="text-sm">{centerName(n.center_id)}</TableCell>
                  <TableCell className="text-sm">
                    {(n.lines || []).slice(0, 2).map((l, i) => (
                      <div key={i}>
                        {l.quantity} × {l.description || `${l.brand} ${l.model}`.trim()}
                      </div>
                    ))}
                    {(n.lines || []).length > 2 && (
                      <div className="text-[#9CA3AF] text-xs">+{(n.lines || []).length - 2} más</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">{euro(total)}</TableCell>
                  <TableCell>
                    <Badge className={status.className}>{status.label}</Badge>
                  </TableCell>
                  <TableCell>
                    {n.attachment_path ? (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAttachment(n.id)}
                          data-testid={`view-att-${n.id}`}
                          className="h-7 px-2 text-[#4A5D4E]"
                        >
                          <Paperclip className="h-3 w-3 mr-1" /> Ver
                        </Button>
                        {canManage && (
                          <label
                            className="cursor-pointer text-[10px] text-[#6E7772] underline"
                            title="Reemplazar adjunto"
                          >
                            cambiar
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,image/*"
                              data-testid={`replace-att-${n.id}`}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleRowAttachmentUpload(n.id, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        )}
                      </div>
                    ) : canManage ? (
                      <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-[#4A5D4E] hover:underline">
                        <Upload className="h-3 w-3" />
                        {uploadingForId === n.id ? "Subiendo…" : "Adjuntar"}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,image/*"
                          data-testid={`upload-att-${n.id}`}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleRowAttachmentUpload(n.id, f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    ) : (
                      <span className="text-xs text-[#9CA3AF]">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {["admin", "center_admin"].includes(user?.role) && (
                      <Button size="icon" variant="ghost" className="text-[#C07F67]" onClick={() => remove(n.id)} data-testid={`del-dn-${n.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
